"use client";
import { socialLoginAction } from "@/actions/user.actions";
import {
  EventNamesEnum,
  LoginTypeEnum,
  SessionStorageKeysEnum,
  WindowEventListenersEnum,
} from "@/constants/constants";
import { useDialectFromClientCookies } from "@/hooks/useDialectFromClientCookies";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import {
  checkIsTruecallerLoginSupported,
  generateCryptoBasedUUID,
  getHomePageRedirectUrlPostLogin,
  getMarketingAttributionFromSessionStorage,
  pollForTruecallerSuccess,
  postLoginEvents,
} from "@/utils/common.helpers";
import { handlePostLoginRedirection } from "./utils/login.helpers";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useUserContext } from "@/context/UserProvider";
import FullScreenLoader from "../FullScreenLoader";

export const TruecallerLogin = () => {
  const { trackEvent, identifyUser } = useTrackingAnalytics();
  const [truecallerLoginSuccess, setTruecallerLoginSuccess] = useState(false);
  const truecallerLoginIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { t, currentLanguage } = useClientTranslations();
  const [loading, setLoading] = useState(false);

  const changeTruecallerLoginSuccess = (value: boolean) => {
    setTruecallerLoginSuccess(value);
  };

  const dialect = useDialectFromClientCookies();

  const router = useRouter();
  const { login } = useUserContext();
  const privacyUrl = "https://www.stage.in/har/privacy";
  const termsUrl = "https://www.stage.in/har/terms";
  const ttl = 1000;
  const loginPrefix = "getstarted";
  const loginSuffix = "login";
  const ctaPrefix = "proceedwith";
  const ctaColor = "#f75d34";
  const ctaTextColor = "#f75d95";
  const btnShape = "round";
  const skipOption = "useanothermethod";
  const type = "btmsheet";
  const partnerName = process.env.NEXT_PUBLIC_TRUECALLER_APP_NAME;
  const partnerKey = process.env.NEXT_PUBLIC_TRUECALLER_APP_KEY;
  const uniqueRequestIdRef = useRef<string | undefined>(undefined); //"e09e90cef30340df84e3c0e13f2b171f"; For testing

  const getUrlSearchParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectionAfterLoginTo =
      urlParams.get("redirectionAfterLoginTo") || "";
    const sourceSplit = redirectionAfterLoginTo?.split("/") ?? [];
    const sourcePage =
      sourceSplit.length > 0 ? sourceSplit[sourceSplit.length - 1] : "";
    return {
      redirectionAfterLoginTo,
      sourcePage,
    };
  };

  useEffect(() => {
    const stopTruecallerLoginPolling = () => {
      if (truecallerLoginIntervalRef.current) {
        clearInterval(truecallerLoginIntervalRef.current);
        truecallerLoginIntervalRef.current = null;
      }
    };

    window.addEventListener(
      WindowEventListenersEnum.INIT_TRUECALLER_LOGIN,
      invokeTruecallerLogin as EventListener,
    );
    window.addEventListener(
      WindowEventListenersEnum.STOP_TRUECALLER_LOGIN_POLLING,
      stopTruecallerLoginPolling as EventListener,
    );

    return () => {
      stopTruecallerLoginPolling();
      window.removeEventListener(
        WindowEventListenersEnum.INIT_TRUECALLER_LOGIN,
        invokeTruecallerLogin as EventListener,
      );
      window.removeEventListener(
        WindowEventListenersEnum.STOP_TRUECALLER_LOGIN_POLLING,
        stopTruecallerLoginPolling as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    if (truecallerLoginSuccess && uniqueRequestIdRef.current) {
      (async () => {
        try {
          setLoading(true);

          // Fetch UTM parameters and deeplink from session storage
          const { utmDetails, deeplink } =
            getMarketingAttributionFromSessionStorage();

          const userDetail = await socialLoginAction({
            requestId: uniqueRequestIdRef.current || "",
            ...(utmDetails && { utmDetails }),
            ...(deeplink && { deeplink }),
          });

          if ("errorMessage" in userDetail) {
            throw new Error(userDetail.errorMessage);
          }

          const { redirectionAfterLoginTo, sourcePage } = getUrlSearchParams();
          if (userDetail._id) {
            (async () => {
              await postLoginEvents(
                userDetail,
                LoginTypeEnum.TRUECALLER,
                sourcePage,
                trackEvent,
                identifyUser,
              );

              const redirectionUrlPostLoginSessionStorage =
                sessionStorage.getItem(
                  SessionStorageKeysEnum.REDIRECTION_URL_POST_LOGIN,
                );

              let redirectUrl =
                redirectionAfterLoginTo ||
                redirectionUrlPostLoginSessionStorage ||
                dialect;
              if (!redirectUrl) {
                redirectUrl = await getHomePageRedirectUrlPostLogin(
                  userDetail.primaryLanguage,
                );
              }

              // Handle post-login redirection
              const finalRedirectUrl = await handlePostLoginRedirection(
                encodeURIComponent(btoa(redirectUrl)),
                login,
              );

              if (finalRedirectUrl) {
                router.replace(finalRedirectUrl);
              } else {
                setLoading(false);
              }
            })();
          }
        } catch (error) {
          console.error("Error in Truecaller login", error);
          setLoading(false);
          // alert(error);
        }
      })();
    }
  }, [truecallerLoginSuccess, uniqueRequestIdRef.current]);

  const invokeTruecallerLogin = async () => {
    uniqueRequestIdRef.current = generateCryptoBasedUUID();

    const isTruecallerLoginSupported = checkIsTruecallerLoginSupported();
    if (!isTruecallerLoginSupported) {
      return;
    }

    const { sourcePage } = getUrlSearchParams();

    trackEvent({
      eventName: EventNamesEnum.LOGIN_INITIATED_TRUECALLER,
      properties: {
        login_method: "truecaller",
        login_stage: "flow_invoked",
        source_page_name: sourcePage,
      },
      sendTo: [],
    });

    try {
      window.location.assign(
        `truecallersdk://truesdk/web_verify?type=${type}&requestNonce=${uniqueRequestIdRef.current}&partnerKey=${partnerKey}&partnerName=${partnerName}&lang=${currentLanguage}&privacyUrl=${privacyUrl}&termsUrl=${termsUrl}&loginPrefix=${loginPrefix}&loginSuffix=${loginSuffix}&ctaPrefix=${ctaPrefix}&ctaColor=${ctaColor}&ctaTextColor=${ctaTextColor}&btnShape=${btnShape}&skipOption=${skipOption}&ttl=${ttl}`,
      );

      // Truecaller app present on the device and the profile overlay opens
      truecallerLoginIntervalRef.current = await pollForTruecallerSuccess({
        uniqueRequestId: uniqueRequestIdRef.current || "",
        changeTruecallerLoginSuccess,
      });
    } catch (error) {
      console.error(error);
      alert("Error invoking Truecaller login");
    }
  };

  if (loading) {
    return <FullScreenLoader />;
  }

  return null;
};
